import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay, faStop, faRotate, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { faDocker } from '@fortawesome/free-brands-svg-icons';
import { useContainerActions } from '../hooks/useContainerActions';

/**
 * ContainerCard Component
 * Displays individual container information and controls
 */
export function ContainerCard({ container, onClick, onActionComplete }) {
  const { start, stop, restart, loading } = useContainerActions(container.id, onActionComplete);

  // State colors
  const stateColors = {
    running: 'bg-green-500',
    paused: 'bg-yellow-500',
    exited: 'bg-red-500',
    created: 'bg-gray-500',
    restarting: 'bg-blue-500',
    removing: 'bg-orange-500',
    dead: 'bg-black'
  };

  const getContainerName = () => {
    if (Array.isArray(container.names) && container.names.length > 0) {
      return container.names[0].replace(/^\//, '');
    }
    return 'unnamed';
  };

  const handleAction = async (e, action) => {
    e.stopPropagation(); // Prevent card click
    try {
      await action();
    } catch (error) {
      // Error is already logged in hook
      alert(`Ошибка: ${error.message}`);
    }
  };

  const canStart = ['exited', 'created'].includes(container.state);
  const canStop = ['running', 'paused'].includes(container.state);

  return (
    <div
      onClick={onClick}
      className="bg-theme-secondary-bg-color rounded-2xl p-4 cursor-pointer hover:shadow-md transition-all"
    >
      {/* Header: Status + Name */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className={`w-3 h-3 rounded-full ${stateColors[container.state] || 'bg-gray-400'}`}></div>
          <span className="font-semibold text-theme-text-color">{getContainerName()}</span>
        </div>
        <span className="text-xs text-theme-hint-color capitalize">{container.state}</span>
      </div>

      {/* Image */}
      <div className="text-sm text-theme-hint-color mb-2 truncate flex items-center gap-2">
        <FontAwesomeIcon icon={faDocker} className="text-theme-link-color" />
        {container.image}
      </div>

      {/* Ports */}
      {container.ports && (
        <div className="text-xs text-theme-hint-color mb-3 truncate">
          <span className="font-medium">Ports:</span> {container.ports || 'none'}
        </div>
      )}

      {/* Status */}
      <div className="text-xs text-theme-hint-color mb-3">
        {container.status}
      </div>

      {/* Action Buttons */}
      <div className="flex gap-2">
        {canStart && (
          <button
            onClick={(e) => handleAction(e, start)}
            disabled={loading}
            className="flex-1 py-2 rounded-lg bg-green-500 text-white hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading ? (
              <FontAwesomeIcon icon={faSpinner} spin />
            ) : (
              <FontAwesomeIcon icon={faPlay} />
            )}
            <span>Start</span>
          </button>
        )}

        {canStop && (
          <>
            <button
              onClick={(e) => handleAction(e, stop)}
              disabled={loading}
              className="flex-1 py-2 rounded-lg bg-red-500 text-white hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <FontAwesomeIcon icon={faSpinner} spin />
              ) : (
                <FontAwesomeIcon icon={faStop} />
              )}
              <span>Stop</span>
            </button>
            <button
              onClick={(e) => handleAction(e, restart)}
              disabled={loading}
              className="flex-1 py-2 rounded-lg bg-blue-500 text-white hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <FontAwesomeIcon icon={faSpinner} spin />
              ) : (
                <FontAwesomeIcon icon={faRotate} />
              )}
              <span>Restart</span>
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default ContainerCard;
